import type { FC } from "react";

import {
  Row,
  Column,
  Text,
  IconButton,
  CloseIcon,
  FormField,
} from "@hightouch/htui";
import moment from "moment";

import { TimeSelect } from "src/ui/time-select";
import { WEEK_DAYS } from "src/utils/constants";

import type { Schedule, ScheduleExpression, VisualCronSchedule } from "./types";

export const LocalDayToUTCDay = (localTime: string, dayIndex: number) => {
  return Number(
    moment(`${dayIndex} ${localTime}`, "e HH:mm").utc().format("e"),
  );
};

export const UTCDayToLocalDay = (utcTime: string, dayIndex: number) => {
  return Number(
    moment.utc(`${dayIndex} ${utcTime}`, `e HH:mm`).local().format("e"),
  );
};

interface Props {
  index: number;
  isDisabled?: boolean;
  hideTimePicker?: boolean;
  schedule: VisualCronSchedule;
  setSchedule: (schedule: Schedule) => void;
}

export const VisualCronExpression: FC<Props> = ({
  index = 0,
  isDisabled = false,
  hideTimePicker = false,
  setSchedule,
  schedule,
}) => {
  const cronTime = moment
    .utc(schedule.schedule?.expressions?.[index]?.time, "HH:mm")
    .local()
    .toDate();

  return (
    <Row
      align="start"
      justify="space-between"
      borderTopWidth={index > 0 ? "hairline" : undefined}
      borderTopStyle={index > 0 ? "solid" : undefined}
      borderColor="base.border"
      pt={index > 0 ? 4 : 0}
      gap={12}
    >
      <Column gap={4}>
        {!hideTimePicker && (
          <FormField label="Repeat at">
            <Column gap={2}>
              <TimeSelect
                isDisabled={isDisabled}
                value={cronTime}
                onChange={(value) => {
                  setSchedule({
                    ...schedule,
                    schedule: {
                      ...schedule?.schedule,
                      expressions: schedule.schedule?.expressions?.map(
                        (exp, idx) => {
                          if (idx === index) {
                            const newTime = moment(value).utc().format("HH:mm");
                            const localTime = moment(value).format("HH:mm");

                            const days = Object.entries(exp.days ?? {})
                              .filter(([_, value]) => Boolean(value))
                              .map(([key]) =>
                                WEEK_DAYS[
                                  LocalDayToUTCDay(
                                    localTime,
                                    UTCDayToLocalDay(
                                      exp.time,
                                      Number(moment(key, "dddd").format("e")),
                                    ),
                                  )
                                ]!.toLowerCase(),
                              )
                              .reduce((state, index) => {
                                state[index] = true;
                                return state;
                              }, {});

                            return {
                              days,
                              time: newTime,
                            };
                          }
                          return exp;
                        },
                      ),
                    },
                  });
                }}
              />
              <Text color="text.tertiary" size="sm">
                Schedules run on UTC time. If your timezone changes with
                daylight saving, the local time of the schedule will shift (e.g.
                1pm EST becomes 2pm EDT).
              </Text>
            </Column>
          </FormField>
        )}

        <FormField label="Repeat on">
          <Row gap={2} bg="white">
            {WEEK_DAYS.map((localDay, dayIndex) => {
              const day =
                WEEK_DAYS[
                  LocalDayToUTCDay(moment(cronTime).format("HH:mm"), dayIndex)
                ]!;
              const selected = schedule?.schedule?.expressions?.[index]?.days
                ? Boolean(
                    schedule.schedule?.expressions?.[index]?.days?.[
                      day.toLowerCase()
                    ],
                  )
                : new Date().getDay() === dayIndex;

              return (
                <Row
                  key={localDay}
                  align="center"
                  justify="center"
                  flexShrink={0}
                  borderRadius="full"
                  boxSize="32px"
                  borderWidth="hairline"
                  borderStyle="solid"
                  borderColor={selected ? "primary.border" : "base.border"}
                  bg={selected ? "primary.base" : "base.lightBackground"}
                  cursor={isDisabled ? "not-allowed" : "pointer"}
                  transitionProperty="background-color, border-color"
                  transitionDuration="fast"
                  transitionTimingFunction="easeOutQuad"
                  sx={{
                    boxShadow: selected
                      ? "0px 1px 1px -1px rgba(0,0,0,0.04), 0px 3px 6px -2px rgba(0,0,0,0.02), inset 0px 0px 0px 1px rgba(255,255,255,0.16), inset 0px 0px 0px 0.5px rgba(0,0,0,0.08)"
                      : "0px 1px 1px -1px rgba(0,0,0,0.04), 0px 3px 6px -2px rgba(0,0,0,0.02), inset 0px 0px 0px 0.5px rgba(0,0,0,0.08)",
                  }}
                  _hover={
                    !isDisabled && !selected
                      ? { bg: "base.background" }
                      : undefined
                  }
                  onClick={() => {
                    if (isDisabled) return;

                    setSchedule({
                      ...schedule,
                      schedule: {
                        ...schedule?.schedule,
                        expressions: schedule.schedule?.expressions?.map(
                          (exp, idx) => {
                            if (idx === index) {
                              const newDays = {
                                ...schedule?.schedule?.expressions?.[index]
                                  ?.days,
                                [day.toLowerCase()]: selected
                                  ? undefined
                                  : true,
                              };

                              return {
                                ...exp,
                                days: newDays,
                              };
                            }
                            return exp;
                          },
                        ),
                      },
                    });
                  }}
                >
                  <Text
                    size="md"
                    fontWeight="medium"
                    color={selected ? "white" : "text.primary"}
                  >
                    {localDay === "Sunday"
                      ? "Su"
                      : localDay === "Saturday"
                        ? "Sa"
                        : localDay.charAt(0)}
                  </Text>
                </Row>
              );
            })}
          </Row>
        </FormField>
      </Column>
      {index > 0 && (
        <IconButton
          aria-label="Remove recurrence"
          isDisabled={isDisabled}
          icon={CloseIcon}
          onClick={() =>
            setSchedule({
              ...schedule,
              schedule: {
                ...schedule?.schedule,
                expressions: schedule.schedule?.expressions?.reduce<
                  ScheduleExpression[]
                >((arr, expression, idx) => {
                  if (idx === index) {
                    return arr;
                  }

                  return [...arr, expression];
                }, []),
              },
            })
          }
        />
      )}
    </Row>
  );
};
